/**
 * Shared, allowlist-based validation for legal-subdomain hosts (e.g.
 * legal.achieve.com, legal-achieve.dev.ffngcp.com) and resolution to their
 * corresponding main-domain host.
 *
 * Used by both `fixSubdomainAssets.ts` (server-rendered content) and
 * `assetUrl.js` (client components) so a single validated allowlist backs
 * every asset URL rewrite — the resolved domain is always one of a fixed,
 * known-good set, never derived by echoing back attacker-supplied Host
 * header content (e.g. a Host of "legal-achieve.attacker.example" must not
 * produce "https://achieve.attacker.example").
 */

const ACHIEVE_LOWER_ENVIRONMENTS = ['dev', 'stg', 'tsg', 'prd']
// FDR only has dev/stg lower environments today (no fdr-tsg.yaml / fdr-prd.yaml
// lower-env values file — production uses the real freedomdebtrelief.com domain).
const FDR_LOWER_ENVIRONMENTS = ['dev', 'stg']

const HYPHEN_ACHIEVE_ENV_PATTERN = new RegExp(
  `^legal-achieve\\.(${ACHIEVE_LOWER_ENVIRONMENTS.join('|')})\\.ffngcp\\.com$`
)
const DOT_ACHIEVE_ENV_PATTERN = new RegExp(
  `^legal\\.achieve\\.(${ACHIEVE_LOWER_ENVIRONMENTS.join('|')})\\.ffngcp\\.com$`
)
const HYPHEN_FDR_ENV_PATTERN = new RegExp(
  `^legal-fdr\\.(${FDR_LOWER_ENVIRONMENTS.join('|')})\\.fdrgcp\\.com$`
)

/**
 * Determines if the current host is a legal subdomain that requires direct
 * asset URL handling.
 *
 * This intentionally matches legal-prefixed hosts for every brand (e.g.
 * legal.freedomdebtrelief.com, legal-fdr.dev.fdrgcp.com), not just Achieve.
 * `getMainDomainUrl` below has an allowlist covering Achieve and FDR host
 * patterns; a legal host for any other brand, or an unrecognized
 * environment, safely falls through to its hardcoded default rather than
 * resolving to a brand-specific domain — tracked as a known gap in
 * ACX-6445, not a regression of this change.
 *
 * @param host - The current host (e.g., 'legal.achieve.com' or 'www.achieve.com')
 * @returns True if this is a legal subdomain
 */
export function isLegalSubdomain(host?: string | null): boolean {
  if (!host) return false

  const normalizedHost = host.split(':')[0].toLowerCase()

  // Check for legal- prefix (dev/staging pattern: legal-achieve.dev.ffngcp.com, legal-achieve.tsg.ffngcp.com)
  if (normalizedHost.startsWith('legal-')) {
    return true
  }

  // Check for legal. prefix (production pattern: legal.achieve.com)
  if (normalizedHost.startsWith('legal.')) {
    return true
  }

  return false
}

/**
 * Gets the main domain URL for the current environment.
 *
 * Only exact, fully-anchored matches against the known host patterns below
 * resolve to a domain derived from the input — any other host (including
 * one merely prefixed with a recognized pattern, e.g.
 * "legal-achieve.attacker.example") falls through to the hardcoded default.
 *
 * @param hostname - The current hostname (e.g., 'legal-achieve.dev.ffngcp.com')
 * @returns The main domain URL for the environment
 *
 * @example
 * getMainDomainUrl('legal-achieve.dev.ffngcp.com') // 'https://achieve.dev.ffngcp.com'
 * getMainDomainUrl('legal-achieve.stg.ffngcp.com') // 'https://achieve.stg.ffngcp.com'
 * getMainDomainUrl('legal-achieve.tsg.ffngcp.com') // 'https://achieve.tsg.ffngcp.com'
 * getMainDomainUrl('legal.achieve.com') // 'https://www.achieve.com'
 * getMainDomainUrl('legal-fdr.dev.fdrgcp.com') // 'https://fdr.dev.fdrgcp.com'
 * getMainDomainUrl('legal.freedomdebtrelief.com') // 'https://www.freedomdebtrelief.com'
 */
export function getMainDomainUrl(hostname?: string | null): string {
  if (!hostname) return 'https://www.achieve.com'

  const normalizedHost = hostname.toLowerCase().split(':')[0]

  // Production: legal.achieve.com → www.achieve.com
  if (normalizedHost === 'legal.achieve.com') {
    return 'https://www.achieve.com'
  }

  // Production: legal.freedomdebtrelief.com → www.freedomdebtrelief.com
  if (normalizedHost === 'legal.freedomdebtrelief.com') {
    return 'https://www.freedomdebtrelief.com'
  }

  // Dev/Staging with hyphen: legal-achieve.{dev,stg,tsg,prd}.ffngcp.com → achieve.{env}.ffngcp.com
  const hyphenAchieveMatch = normalizedHost.match(HYPHEN_ACHIEVE_ENV_PATTERN)
  if (hyphenAchieveMatch) {
    return `https://achieve.${hyphenAchieveMatch[1]}.ffngcp.com`
  }

  // Staging with dot: legal.achieve.{dev,stg,tsg,prd}.ffngcp.com → achieve.{env}.ffngcp.com
  const dotAchieveMatch = normalizedHost.match(DOT_ACHIEVE_ENV_PATTERN)
  if (dotAchieveMatch) {
    return `https://achieve.${dotAchieveMatch[1]}.ffngcp.com`
  }

  // Dev/Staging FDR: legal-fdr.{dev,stg}.fdrgcp.com → fdr.{env}.fdrgcp.com
  const hyphenFdrMatch = normalizedHost.match(HYPHEN_FDR_ENV_PATTERN)
  if (hyphenFdrMatch) {
    return `https://fdr.${hyphenFdrMatch[1]}.fdrgcp.com`
  }

  // Default to dev environment for any other legal subdomain
  return 'https://achieve.dev.ffngcp.com'
}
